import { useState, useEffect } from 'react';
import {
  Form, Input, Select, Checkbox,
} from '@skyline-platform/alchemy';
import { FormFooter } from '../FormFooter/FormFooter';
import {
  validateStringLength,
  validateIntBounds,
  validateIPOrFQDN,
} from '@utils/formValidators';
import './CameraForm.css';
import '../FullScreenForm.css';
import * as is from 'is';
import {
  getDevices, saveDevice, updateDevice,
} from '@utils/graphql/creators/devices';
import {
  createMetadata, updateMetadata, deleteMetadata,
} from '@utils/graphql/creators/metadata';
import PropTypes from 'prop-types';
import { compareData } from '@utils/objectHandler';
import {
  assignValue, camelToHeader, getLogger,
} from '@utils/general';
import { useDebouncedCallback } from 'use-debounce';
import { InfoIcon } from '../../InfoIcon/InfoIcon';
import { MetadataForm } from '../MetadataForm/MetadataForm';
import { notifications } from '@utils/notifications/index';
import { getAddrLabel } from '@pages/utils/helpers';
import { IPv4 } from '@pages/split/utils/Constants';
import { getConfig } from '@utils/config';

const logger = getLogger('src.components.CameraForm');

const PASSWORD_PLACEHOLDER = '********';

const VALID_CAMERA_TYPES = [
  'Static Camera',
  'Mobile Camera',
  'Other',
];

const CONNECTION_TYPE_OPTIONS = [
  'Radio',
  'Cellular',
  'Fiber',
];

/**
 * Pure validation function for camera form fields.
 * Returns per-field validation results and a combined isValid flag.
 *
 * @param {object} inputs
 * @param {string} inputs.name
 * @param {string} inputs.description
 * @param {string} inputs.type
 * @param {string} inputs.networkAddr
 * @param {string|null} inputs.username
 * @param {string|null} inputs.password
 * @param {string} inputs.lat
 * @param {string} inputs.long
 * @param {boolean} [inputs.deviceNameExists=false]
 * @param {boolean} [inputs.metadataValid=true]
 * @param {string} [inputs.connection_type='']
 * @param {boolean} [inputs.connectionTypeEnabled=false]
 * @returns {{ fields: object, isValid: boolean }}
 */
export function validateCameraFields({
  name,
  description,
  type,
  networkAddr,
  username,
  password,
  lat,
  long,
  deviceNameExists = false,
  metadataValid = true,
  connection_type = '',
  connectionTypeEnabled = false,
}) {
  const nameValid = is.empty(name)
    ? { isValid: false, error: null }
    : deviceNameExists
      ? { isValid: false, error: 'Camera name already exists. Please update the camera name.' }
      : validateStringLength(name, 1, 32, 'Name');
  const descValid = validateStringLength(description, 0, 140, 'Description');
  const typeValid = is.empty(type)
    ? { isValid: false, error: null }
    : {
      isValid: VALID_CAMERA_TYPES.includes(type),
      error: 'Type must be one of the provided options.',
    };
  const connectionTypeValid = !connectionTypeEnabled || is.empty(connection_type)
    ? { isValid: true, error: null }
    : { isValid: CONNECTION_TYPE_OPTIONS.includes(connection_type), error: 'Connection Type must be one of the provided options.' };
  const networkAddrValid = is.empty(networkAddr)
    ? { isValid: false, error: null }
    : validateIPOrFQDN(networkAddr, IPv4);
  const latValid = !is.empty(lat) ? validateIntBounds(lat, -90, 90, 'this field') : { isValid: true, error: null };
  const longValid = !is.empty(long)
    ? validateIntBounds(long, -180, 180, 'this field')
    : { isValid: true, error: null };
  const usernameValid = is.empty(username)
    ? !is.empty(password)
      ? { isValid: false, error: 'Camera with a password must have a username' }
      : { isValid: true, error: '' }
    : { isValid: true, error: '' };
  const passwordValid = is.empty(password)
    ? !is.empty(username)
      ? { isValid: false, error: 'Camera with a username must have a password' }
      : { isValid: true, error: '' }
    : { isValid: true, error: '' };

  return {
    fields: {
      name: nameValid,
      description: descValid,
      type: typeValid,
      networkAddr: networkAddrValid,
      lat: latValid,
      long: longValid,
      username: usernameValid,
      password: passwordValid,
      connection_type: connectionTypeValid,
    },
    isValid: (
      nameValid.isValid &&
      descValid.isValid &&
      networkAddrValid.isValid &&
      typeValid.isValid &&
      latValid.isValid &&
      longValid.isValid &&
      usernameValid.isValid &&
      passwordValid.isValid &&
      connectionTypeValid.isValid &&
      metadataValid
    ),
  };
}

async function getDevicesByNetworkAddress(addr) {
  let devices = [];
  const { query, execute } = getDevices(null, { network_address: addr });
  query.addReturnProperties('network_address');

  await execute()
    .then(({
      error, data,
    }) => {
      if (error) {
        logger.error(error);
      }
      if (data) {
        devices = data;
      }
    })
    .catch(() => {
      logger.error(`Failed to retrieve cameras with network address ${addr}.`);
    });

  return devices;
}

async function handleSave(inputs) {
  const { mutation, execute } = saveDevice('create_device', inputs);
  mutation.addReturnProperties('id', 'name');

  let device;
  await execute()
    .then(({
      error, data,
    }) => {
      if (error) {
        const failMessage = `Failed to create camera ${inputs.name}.`;
        notifications.error(failMessage);
        logger.error(error, failMessage);
      }
      if (data) {
        device = data;
      }
    });

  return device;
}

async function handleEdit(cameraId, data) {
  const { mutation, execute } = updateDevice('update_device', cameraId, data);
  mutation.addReturnProperties('id', 'name');
  const failMessage = 'Failed to edit camera.';

  await execute()
    .then(({ error }) => {
      if (error) {
        notifications.error(failMessage);
        logger.error(error, failMessage);
      }
    });
}

export async function isDuplicateNetworkAddr(addr) {
  const addresses = await getDevicesByNetworkAddress(addr);
  return addresses.length > 0;
}

export const CameraForm = ({
  cameraData,
  handleClose,
  setFormData,
}) => {
  const initErrorState = {
    name: { isValid: false, error: '' },
    description: { isValid: false, error: '' },
    type: { isValid: false, error: '' },
    networkAddr: { isValid: false, error: '' },
    username: { isValid: false, error: '' },
    password: { isValid: false, error: '' },
    lat: { isValid: false, error: '' },
    long: { isValid: false, error: '' },
    elevation: { isValid: false, error: '' },
    connection_type: { isValid: true, error: '' },
  };
  const connectionTypeEnabled = getConfig().CAMERA_CONNECTION_TYPE_ENABLED === 'true';
  const [duplicateNetworkAddr, setDuplicateNetworkAddr] = useState(false);
  const [error, setError] = useState(initErrorState);
  const [showWarning, setShowWarning] = useState(false);

  // Inputs
  const [name, setName] = useState(cameraData?.name || '');
  const [description, setDescription] = useState(cameraData?.description || '');
  const [type, setType] = useState(
    cameraData
      ? cameraData?.type === 'Unknown'
        ? 'Other'
        : camelToHeader(cameraData.type)
      : ''
  );
  const placeholderPassword = cameraData?.has_creds ? PASSWORD_PLACEHOLDER : '';
  const [networkAddr, setNetworkAddr] = useState(cameraData?.network_address || '');
  const [username, setUsername] = useState(cameraData?.username || null);
  const [password, setPassword] = useState(placeholderPassword || null);
  const [lat, setLat] = useState(cameraData?.latitude || '');
  const [long, setLong] = useState(cameraData?.longitude || '');
  const [elevation, setElevation] = useState(cameraData?.elevation || '');
  const [connectionType, setConnectionType] = useState(cameraData?.connection_type || '');
  const [problemCamera, setProblemCamera] = useState(cameraData?.problem_camera || false);
  const [metadata, setMetadata] = useState(() => {
    if (!cameraData?.metadata) return {};
    if (Array.isArray(cameraData.metadata)) {
      const result = {};
      cameraData.metadata.forEach(({ key, value }) => { result[key] = value; });
      return result;
    }
    return JSON.parse(cameraData.metadata);
  });
  const [metadataPrivacy, setMetadataPrivacy] = useState(() => {
    if (!cameraData?.metadata || !Array.isArray(cameraData.metadata)) return {};
    const result = {};
    cameraData.metadata.forEach(({ key, private: isPrivate }) => { result[key] = isPrivate || false; });
    return result;
  });
  const [meatadataValid, setMetadataValid] = useState(true);
  const [allFieldsValid, setAllFieldsValid] = useState(false);
  const [deviceNameExists, setDeviceNameExists] = useState(false);

  // Validate input fields
  const validateInputs = () => {
    const { fields, isValid } = validateCameraFields({
      name,
      description,
      type,
      networkAddr,
      username,
      password,
      lat,
      long,
      deviceNameExists,
      metadataValid: meatadataValid,
      connection_type: connectionType,
      connectionTypeEnabled,
    });

    setError({
      name: fields.name,
      description: fields.description,
      type: fields.type,
      networkAddr: fields.networkAddr,
      lat: fields.lat,
      long: fields.long,
      username: fields.username,
      password: fields.password,
      connection_type: fields.connection_type,
    });

    return isValid;
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern
    const validInputs = validateInputs();


    setAllFieldsValid(validInputs);
  }, [
    name,
    description,
    type,
    networkAddr,
    username,
    password,
    lat,
    long,
    elevation,
    problemCamera,
    metadata,
    meatadataValid,
    connectionType,
    deviceNameExists,
  ]);

  const checkDuplicateDeviceName = async (name, setDuplicateInfo) => {
    const { query, execute } = getDevices(null, { name });
    query.addReturnProperties('success', 'error', 'id', 'name');
    let cameraNameDupe = false;

    await execute()
      .then(({ error, data }) => {
        if (error) {
          logger.error(error);
        }

        /**
         * When creating a new camera, cameraData will be undefined
         * On edit, cameraData is defined so only show duplicate
         * error if the camera name is changed to an existing one.
         */
        if (data?.length > 0 && (is.undefined(cameraData) || cameraData.id !== data[0].id)) {
          cameraNameDupe = true;
        }
      })
      .catch(() => {
        logger.error(
          `There was a failure retrieving data for device ${name}`
        );
      });
    setDuplicateInfo(cameraNameDupe);
  };

  const debouncedCheckDuplicateDeviceName = useDebouncedCallback(checkDuplicateDeviceName, 300);

  useEffect(() => {
    debouncedCheckDuplicateDeviceName(name, setDeviceNameExists);
  }, [name]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern, sync update is intentional
    setShowWarning(duplicateNetworkAddr);
  },
  [duplicateNetworkAddr]);

  const handleNetworkAddrBlur = async () => {
    if (networkAddr) {
      const isDuplicate = await isDuplicateNetworkAddr(networkAddr);
      setDuplicateNetworkAddr(isDuplicate);
    } else {
      setDuplicateNetworkAddr(false);
    }
  };

  const handleCamera = async (apiChange) => {
    validateInputs();

    const cameraToSave = {
      name,
      description,
      type: type === 'Other' ? 'Unknown' : type.replace(/\s+/g, ''),
      network_address: networkAddr,
      username: username || null,
      problem_camera: problemCamera,
    };

    // Only include password when it has been explicitly changed.
    // If the camera has stored credentials and the field still shows the
    // placeholder, omit password so the backend does not overwrite it.
    if (!cameraData?.has_creds || password !== PASSWORD_PLACEHOLDER) {
      cameraToSave.password = password || null;
    }

    cameraToSave.latitude = assignValue(lat);
    cameraToSave.longitude = assignValue(long);
    cameraToSave.elevation = assignValue(elevation);
    if (connectionTypeEnabled) {
      cameraToSave.connection_type = connectionType || null;
    }

    switch (apiChange) {
      case 'newCamera': {
        try {
          const savedCamera = await handleSave(cameraToSave);

          // Create metadata entries separately
          if (!is.empty(metadata)) {
            const metadataEntries = Object.entries(metadata)
              .filter(([, value]) => value !== null && value !== '');
            await Promise.all(metadataEntries.map(([key, value]) => {
              const { execute } = createMetadata({
                key,
                value,
                model_id: savedCamera.id,
                private: metadataPrivacy[key] || false,
              });
              return execute();
            }));
          }

          setFormData({
            ...cameraToSave, id: savedCamera.id, name: getAddrLabel(cameraToSave, 'network_address'),
          }, false, true);
          notifications.success(`Successfully created camera ${savedCamera.name}.`);
          handleClose();
        } catch (error) {
          const failMessage = `Failed to create camera ${cameraToSave.name}.`;
          notifications.error(failMessage);
          logger.error(error, failMessage);
        }
        break;
      }
      case 'editCamera': {
        const updatedData = compareData(cameraData, cameraToSave);
        const updatedCameraName = updatedData.name || cameraData.name;

        try {
          // Only call update_device if device fields actually changed
          if (!is.empty(updatedData)) {
            await handleEdit(cameraData.id, updatedData);
          }

          // Handle metadata mutations separately
          const oldMetadata = Array.isArray(cameraData?.metadata)
            ? cameraData.metadata
            : [];
          const oldMetadataByKey = {};
          oldMetadata.forEach((entry) => { oldMetadataByKey[entry.key] = entry; });

          const metadataPromises = [];

          // Update or create metadata entries
          Object.entries(metadata).forEach(([key, value]) => {
            const existing = oldMetadataByKey[key];
            const isPrivate = metadataPrivacy[key] || false;

            if (existing) {
              // Update if value or private flag changed
              if (existing.value !== value || existing.private !== isPrivate) {
                const { execute } = updateMetadata(existing.id, {
                  key,
                  value,
                  private: isPrivate,
                });
                metadataPromises.push(execute());
              }
            } else if (value !== null && value !== '') {
              // Create new metadata entry
              const { execute } = createMetadata({
                key,
                value,
                model_id: cameraData.id,
                private: isPrivate,
              });
              metadataPromises.push(execute());
            }
          });

          // Remove metadata entries that no longer exist
          oldMetadata.forEach((entry) => {
            if (!Object.prototype.hasOwnProperty.call(metadata, entry.key)) {
              const { execute } = deleteMetadata(entry.id);
              metadataPromises.push(execute());
            }
          });

          await Promise.all(metadataPromises);

          notifications.success(`Successfully updated camera ${updatedCameraName}.`);
          handleClose();
        }
        catch (error) {
          const failMessage = 'Failed to edit camera.';
          notifications.error(failMessage);
          logger.error(error, failMessage);
        }
      }
    }
  };

  function handleCancel() {
    setError(initErrorState);
    handleClose();
  }

  return (
    <gar-camera-form>
      <Form
        formTitle='Create New Camera'
        footerPlacement='right'
        footer={[
          <FormFooter
            key='footer'
            onCancel={handleCancel}
            onSubmit={() => handleCamera(cameraData ? 'editCamera' : 'newCamera')}
            submitLabel={cameraData ? 'Update Camera' : 'Create New Camera'}
            isValid={allFieldsValid}
          />,
        ]}
      >
        <Input
          id='input-name'
          inputLabel='Name'
          required
          helperText='Camera Name'
          value={name}
          key='input-name'
          onChange={(e) => setName(e.target.value)}
          error={error.name.isValid ? undefined : 'true'}
          errorMessage={error.name.isValid ? '' : error.name.error}
        />
        <Input
          id='input-description'
          inputLabel='Description'
          helperText='Description of the Camera'
          value={description}
          key='input-description'
          onChange={(e) => setDescription(e.target.value)}
          error={error.description.isValid ? undefined : 'true'}
          errorMessage={error.description.isValid ? '' : error.description.error}
          multiline
          rows={6}
        />
        <Select
          id='input-type'
          options={VALID_CAMERA_TYPES}
          placeholder='Select One'
          lineSeparator
          label='Camera Type'
          error={error.type.isValid ? undefined : 'true'}
          errorMessage={error.type.isValid ? '' : error.type.error}
          onSelectChange={(val) => setType(val)}
          value={type || null}
          required
        />
        {connectionTypeEnabled && (
          <Select
            id='input-connection-type'
            options={CONNECTION_TYPE_OPTIONS}
            placeholder='Select One'
            lineSeparator
            label='Connection Type'
            error={error.connection_type.isValid ? undefined : 'true'}
            errorMessage={error.connection_type.isValid ? '' : error.connection_type.error}
            onSelectChange={(val) => setConnectionType(val)}
            value={connectionType || null}
          />
        )}
        <Input
          id='input-network-addr'
          inputLabel='Network Address'
          required
          helperText='IPv4 address or FQDN'
          value={networkAddr}
          onChange={(e) => setNetworkAddr(e.target.value)}
          onBlur={() => handleNetworkAddrBlur()}
          useWarning={showWarning}
          key={showWarning} // Add a key prop to force re-render
          warningMessage={`This address is already in use by another connection. Proceeding may result in
            duplicate streams. If you are setting up multiple cameras or testing, ensure this is intentional.`}
          error={error.networkAddr.isValid ? undefined : 'true'}
          errorMessage={error.networkAddr.isValid ? '' : error.networkAddr.error}
        />
        <Input
          id='input-username'
          inputLabel='Username'
          helperText='Username to access the camera'
          key='input-username'
          autocomplete='off'
          onChange={(e) => setUsername(e.target.value)}
          value={username}
          error={error.username.isValid.toString()}
          errorMessage={error.username.error}
        />
        <Input
          id='input-password'
          inputLabel='Password'
          helperText='Password to access the camera'
          key='input-password'
          autocomplete='off'
          onChange={(e) => setPassword(e.target.value)}
          type='password'
          value={password}
          error={error.password.isValid.toString()}
          errorMessage={error.password.error}
        />
        <Input
          id='input-lat'
          inputLabel='Latitude'
          helperText='Latitude between -90 and 90'
          key='input-lat'
          onChange={(e) => setLat(e.target.value)}
          error={error.lat.isValid ? undefined : 'true'}
          errorMessage={error.lat.isValid ? '' : error.lat.error}
          value={lat}
          type='number'
        />
        <Input
          id='input-long'
          inputLabel='Longitude'
          helperText='Longitude between -180 and 180'
          key='input-long'
          onChange={(e) => setLong(e.target.value)}
          error={error.long.isValid ? undefined : 'true'}
          errorMessage={error.long.isValid ? '' : error.long.error}
          value={long}
          type='number'
        />
        <Input
          id='input-elevation'
          inputLabel='Elevation'
          helperText='Elevation value'
          key='input-elevation'
          onChange={(e) => setElevation(e.target.value)}
          value={elevation}
          type='number'
        />
        <div className='problem-stream-wrapper'>
          <Checkbox
            label='Problem Camera/Device'
            labelPlacement='start'
            handleChange={() => { setProblemCamera((prev) => !prev); }}
            checked={problemCamera}
          />
          <InfoIcon
            id='problem-camera'
            label='Show description for Problem Stream'
            desc='This camera or device is known to have issues at a hardware level.'
          />
        </div>
        <MetadataForm
          metadataFormData={metadata}
          metadataPrivacy={metadataPrivacy}
          setMetadata={setMetadata}
          setMetadataValid={setMetadataValid}
          setMetadataPrivacy={setMetadataPrivacy}
        />
      </Form>
    </gar-camera-form>
  );
};

CameraForm.propTypes = {
  cameraData: PropTypes.object,
  handleClose: PropTypes.func,
  setFormData: PropTypes.func,
};
